import { ReferencedEntry } from 'contentful';
import { FormikProps } from 'formik';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import { FormField, FormFields, PredefinedFormField } from 'src/api/contentful/contentTypes.cloverdotcom';

import { BusyLoader } from '../BusyLoader/BusyLoader';
import { SubmitButton } from '../Form/SubmitButton';

import {
  AddressLine2Field, BankAccountLast4Field, BusinessAddressLine1Field, BusinessNameField, CityField, DropdownField, EmailField,
  FirstNameField, GoogleRecaptcha, HiddenField, LastNameField, MidField, MobilePhoneNumberField, PhoneNumberField, RequiredCheckbox, SsnLast4Field,
  StateField, ZipCodeField
} from './Fields';

import { getField } from 'src/utils/EntryUtil';
import { SessionStorage } from 'src/utils/SessionStorage';
import { Blurb } from '../Contentful/Blurb';
import style from './ContactForm.scss';
import { Persist } from './PersistFormValues';

export type ContactInfo = {
  emailAddress?: string;
  firstName?: string;
  lastName?: string;
  businessName?: string;
  phoneNumber?: string;
};

export type FieldsList = {
  fields?: FormField[];
  isLoadingReferralInfo?: boolean;
  isFormPrefilled?: boolean;
};

type FormProps = 'initialValues' | 'values' | 'errors' | 'handleChange' | 'setFieldTouched' | 'setFieldValue' | 'touched' | 'handleSubmit' | 'isSubmitting';

export type ContactFormBodyProps = Pick<FormikProps<ContactInfo>, FormProps> & FieldsList;

function createFieldsSection (fields: FormField[], fieldMapping: any) {
  const formFields = fields.reduce((acc: JSX.Element[], field) => {
    if (field.fieldType && fieldMapping[field.fieldType]) acc = acc.concat(fieldMapping[field.fieldType]);
    return acc;
  }, []);
  return formFields;
}

// This is a IE11 polyfill for .find()
export const findFirstFieldOfType = (fields: FormField[], fieldType: string) => {
  for (const formField of fields) {
    if (formField.fieldType === fieldType) return formField;
  }
  return null;
};

const GTM_SELECTOR = 'contact-form';

export const ContactFormBody: React.FC<ContactFormBodyProps> = ({initialValues, values, errors, handleChange, setFieldTouched, setFieldValue, touched, handleSubmit, isSubmitting,
  fields = [{ fieldType: FormFields.FIRST_AND_LAST_NAME }, { fieldType: FormFields.EMAIL }, { fieldType: FormFields.BUSINESS_NAME }, { fieldType: FormFields.PHONE_NUMBER }], isLoadingReferralInfo, isFormPrefilled }) => {

  useEffect(() => {
    Object.keys(initialValues).forEach(key => {
      if (values[key] === '') return;
      setFieldTouched(key, true);
    });
  }, [values]);

  const { t } = useTranslation();

  if (isSubmitting || isLoadingReferralInfo) { return <BusyLoader className={style.loader} />; }

  const generateInputProps = (valueKey: string) => ({
    name: valueKey,
    id: valueKey,
    value: values[valueKey],
    onChange: handleChange,
    onBlur: (e: any) => {
      const value = e.target.value;
      setFieldTouched(valueKey, true);
      setFieldValue(valueKey, value.trim());
    },
    classes: { underline: style.inputFocused },
  });

  const generateDropdownInputProps = (valueKey: string) => ({
    name: valueKey,
    id: valueKey,
    value: values[valueKey],
    onChange: handleChange,
    onBlur: () => setFieldTouched(valueKey, true),
  });

  const generateHiddenInputProps = (predefinedField: ReferencedEntry<PredefinedFormField>) => {
    const valueKey = getField(predefinedField, 'key');
    if (!valueKey) return {};

    return {
      name: valueKey,
      id: valueKey,
      value: values[valueKey!],
    };
  };

  const getFieldOptions = (fieldType: string) => {
    const field = findFirstFieldOfType(fields, fieldType);

    if (!field || !field.options) return {};

    return field.options.items;
  };

  const getFieldDisplayDetail = (fieldType: string) => {
    const field = findFirstFieldOfType(fields, fieldType);

    if (!field || !field.displayDetail) return undefined;

    return field.displayDetail;
  };

  const getPredefinedField = (fieldType: string) => {
    const field = findFirstFieldOfType(fields, fieldType);

    return field?.predefinedField;
  };

  const firstName = (
    <FirstNameField generateInputProps={generateInputProps} touched={touched} errors={errors} key="first-name" />
  );

  const lastName = (
    <LastNameField generateInputProps={generateInputProps} touched={touched} errors={errors} key="last-name" />
  );

  const emailAddress = (
    <EmailField
      generateInputProps={generateInputProps}
      touched={touched}
      errors={errors}
      key="email-address"
      disabled={isFormPrefilled}
    />
  );

  const businessName = (
    <BusinessNameField generateInputProps={generateInputProps} touched={touched} errors={errors} key="business-name" />
  );

  const businessAddressLine1 = (
    <BusinessAddressLine1Field generateInputProps={generateInputProps} touched={touched} errors={errors} key="business-address-line1" />
  );

  const addressLine2 = (
    <AddressLine2Field generateInputProps={generateInputProps} touched={touched} errors={errors} key="address-line2" />
  );

  const city = (
    <CityField generateInputProps={generateInputProps} touched={touched} errors={errors} key="city" />
  );

  const state = (
    <StateField generateInputProps={generateInputProps} touched={touched} errors={errors} values={values} key="state" />
  );

  const zipCode = (
    <ZipCodeField generateInputProps={generateInputProps} touched={touched} errors={errors} key="zip-code" />
  );

  const phoneNumber = (
    <PhoneNumberField generateInputProps={generateInputProps} touched={touched} errors={errors} key="phone-number" />
  );

  const mobilePhoneNumber = (
    <MobilePhoneNumberField generateInputProps={generateInputProps} touched={touched} errors={errors} key="mobile-phone-number" />
  );

  const ssnLast4 = (
    <SsnLast4Field generateInputProps={generateInputProps} touched={touched} errors={errors} key="ssn-last-4" />
  );

  const bankAccountLast4 = (
    <BankAccountLast4Field generateInputProps={generateInputProps} touched={touched} errors={errors} key="bank-account-last-4" />
  );

  const mid = (
    <MidField generateInputProps={generateInputProps} touched={touched} errors={errors} key="mid" />
  );

  const googleRecaptcha = (
    <GoogleRecaptcha setFieldValue={setFieldValue} key="google-recaptcha" />
  );

  const businessType = (
    <DropdownField
      generateInputProps={generateDropdownInputProps}
      touched={touched}
      errors={errors}
      key="business-type"
      options={getFieldOptions(FormFields.BUSINESS_TYPE)}
      formFieldLabel={FormFields.BUSINESS_TYPE}
      valueKey="businessType"
      values={values}
    />
  );

  const yearsInBusiness = (
    <DropdownField
      generateInputProps={generateDropdownInputProps}
      touched={touched}
      errors={errors}
      key="years-in-business"
      options={getFieldOptions(FormFields.YEARS_IN_BUSINESS)}
      formFieldLabel={FormFields.YEARS_IN_BUSINESS}
      valueKey="yearsInBusiness"
      values={values}
    />
  );

  const requiredCheckbox = (
    <RequiredCheckbox
      generateInputProps={generateInputProps}
      touched={touched}
      errors={errors}
    >
      <Blurb
        key="display-text-blurb"
        entry={getFieldDisplayDetail(FormFields.REQUIRED_CHECKBOX)}
        field="blurb"
        tagName="div"
        className={style.displayTextField}
      />
    </RequiredCheckbox>
  );

  const displayText = (
    <Blurb
      key="display-text-blurb"
      entry={getFieldDisplayDetail(FormFields.DISPLAY_TEXT)}
      field="blurb"
      tagName="div"
      className={style.displayTextField}
    />
  );

  const annualProcessingVolume = (
    <DropdownField
      generateInputProps={generateDropdownInputProps}
      touched={touched}
      errors={errors}
      key="annual-processing-volume"
      options={getFieldOptions(FormFields.ANNUAL_PROCESSING_VOLUME)}
      formFieldLabel={FormFields.ANNUAL_PROCESSING_VOLUME}
      valueKey="annualProcessingVolume"
      values={values}
    />
  );

  const whenToCall = (
    <DropdownField
      generateInputProps={generateDropdownInputProps}
      touched={touched}
      errors={errors}
      key="when-to-call"
      options={getFieldOptions(FormFields.WHEN_TO_CALL)}
      formFieldLabel="When should we call?"
      valueKey="whenToCall"
      values={values}
    />
  );

  const hiddenField = (
    <HiddenField
      generateInputProps={generateHiddenInputProps}
      key="hidden-text"
      predefinedField={getPredefinedField(FormFields.HIDDEN_FIELD)}
    />
  );

  const fieldToElementMapping = {
    [FormFields.BUSINESS_ADDRESS]: [businessAddressLine1, addressLine2, city, state, zipCode],
    [FormFields.BUSINESS_NAME]: businessName,
    [FormFields.EMAIL]: emailAddress,
    [FormFields.FIRST_AND_LAST_NAME]: [firstName, lastName],
    [FormFields.PHONE_NUMBER]: phoneNumber,
    [FormFields.MOBILE_PHONE_NUMBER]: mobilePhoneNumber,
    [FormFields.ZIP_CODE]: zipCode,
    [FormFields.BUSINESS_TYPE]: businessType,
    [FormFields.YEARS_IN_BUSINESS]: yearsInBusiness,
    [FormFields.SSN_LAST_4]: ssnLast4,
    [FormFields.BANK_ACCOUNT_LAST_4]: bankAccountLast4,
    [FormFields.MID]: mid,
    [FormFields.GOOGLE_RECAPTCHA]: googleRecaptcha,
    [FormFields.REQUIRED_CHECKBOX]: requiredCheckbox,
    [FormFields.DISPLAY_TEXT]: displayText,
    [FormFields.ANNUAL_PROCESSING_VOLUME]: annualProcessingVolume,
    [FormFields.WHEN_TO_CALL]: whenToCall,
    [FormFields.HIDDEN_FIELD]: hiddenField,
  };

  const formFieldsSection = createFieldsSection(fields, fieldToElementMapping);
  if (!formFieldsSection.length) return null;

  const formPath = window.location.pathname.indexOf('clover-account') !== -1 ? 'clover-account' : window.location.pathname;
  const isSubmitDisabled = !!(Object.keys(errors).length) || isSubmitting;
  const trimBeforeSubmit = () => {
    Object.keys(values).forEach(key => {
      if (typeof (values[key]) === 'string') {
        setFieldValue(key, values[key].trim());
      }
    });
    handleSubmit();
  };

  return (
    <form
      onSubmit={e => {
        // on safari, form refreshes after form submit. preventDefault() to override this behavior and not refresh the page
        e.preventDefault();
        trimBeforeSubmit();
        SessionStorage.tryRemoveItem(formPath);
      }}
      className={style.contactForm}
      data-gtm-selector={GTM_SELECTOR}
    >
      <Persist name={formPath} />
      <Grid container spacing={3}>
        {formFieldsSection}
        <Grid item xs={12} classes={{ item: style.gridInput }}>
          <SubmitButton className={style.submitButton} disabled={isSubmitDisabled}>{t('submit')}</SubmitButton>
        </Grid>
      </Grid>
    </form>
  );
};
